import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import patientService from '../services/patientService';
import appointmentService from '../services/appointmentService';
import treatmentService from '../services/treatmentService';

// Move StatCard outside Dashboard
const StatCard = ({ title, value, icon, color, bgColor, loading }) => (
  <div className={`${bgColor} rounded-lg shadow-md p-6`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-600 text-sm font-medium">{title}</p>
        {loading ? (
          <div className="h-9 w-16 bg-gray-200 animate-pulse rounded mt-2"></div>
        ) : (
          <p className={`text-3xl font-bold ${color} mt-2`}>{value}</p>
        )}
      </div>
      <div className={`text-4xl ${color}`}>{icon}</div>
    </div> 
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalPatients: 0,
    todayAppointments: 0,
    scheduledAppointments: 0,
    completedAppointments: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch all stats in parallel
      const [patientsResponse, appointmentsStatsResponse, treatmentsStatsResponse] = await Promise.allSettled([
        patientService.getAllPatients({ per_page: 1 }),
        appointmentService.getAppointmentStats(),
        treatmentService.getTreatmentStats().catch(() => ({ data: { total: 0 } }))
      ]);

      // Extract patient total
      const totalPatients = patientsResponse.status === 'fulfilled' 
        ? patientsResponse.value.data.total || 0 
        : 0;

      // Extract appointment stats
      const appointmentStats = appointmentsStatsResponse.status === 'fulfilled'
        ? appointmentsStatsResponse.value.data
        : { today: 0, scheduled: 0, completed: 0 };

      setStats({
        totalPatients,
        todayAppointments: appointmentStats.today || 0,
        scheduledAppointments: appointmentStats.scheduled || 0,
        completedAppointments: appointmentStats.completed || 0,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      
      <div className="flex">
        <Sidebar />
        
        <div className="flex-1 p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Welcome to KESHAV CARE Clinic Management System
            </p>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Total Patients"
              value={stats.totalPatients}
              color="text-blue-600"
              bgColor="bg-blue-50"
              loading={loading}
            />
            <StatCard
              title="Today's Appointments"
              value={stats.todayAppointments}
              color="text-green-600"
              bgColor="bg-green-50"
              loading={loading}
            />
            <StatCard
              title="Scheduled"
              value={stats.scheduledAppointments}
              color="text-yellow-600"
              bgColor="bg-yellow-50"
              loading={loading}
            />
            <StatCard
              title="Completed"
              value={stats.completedAppointments}
              color="text-purple-600"
              bgColor="bg-purple-50"
              loading={loading}
            />
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => navigate('/patients')}
                className="flex items-center justify-center space-x-2 p-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <span className="font-medium">Manage Patients</span>
              </button>
              <button
                onClick={() => navigate('/appointments')}
                className="flex items-center justify-center space-x-2 p-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <span className="font-medium">Schedule Appointment</span>
              </button>
              <button
                onClick={() => navigate('/treatments')}
                className="flex items-center justify-center space-x-2 p-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <span className="font-medium">Add Treatment</span>
              </button>
            </div>
          </div>

          {/* Feature Status */}
          
        </div>
      </div>
    </div>
  );
};

export default Dashboard;